import { IWorkspaceSearcher } from '../components/WorkspaceSelectorViewHeading'
import { action, computed, makeObservable, observable } from 'mobx'
import { GetCurrentUserWorkspaceMemberships } from '../iam/WorkspaceMembershipStore'
import Workspace from '../iam/models/Workspace'
import { SingleSelectFilter } from '../infra/FilterControls/SingleSelect/SingleSelectFilter'
import { GetCurrentMember } from '../iam/SessionStore'
import { Subscription } from 'rxjs'
import { SearchTextViewModel } from '../infra/SearchTextViewModel'
import WorkspaceSelectorState from '../iam/views/AuthenticationContainer/WorkspaceSelector/WorkspaceSelectorState'

export class WorkspaceSelectorViewModel implements IWorkspaceSearcher {
  private searchSubscription = new Subscription()

  private textSearcher = new SearchTextViewModel(500)

  @observable
  editing = false

  constructor(
    private getCurrentMember: GetCurrentMember,
    private getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private workspaceFilter: () => SingleSelectFilter<string, Workspace>,
    private clearSelectedUsers: () => void,
    private clearWorkspaceScopedFilters: () => void,
    private workspaceSelectorState: WorkspaceSelectorState,
  ) {
    makeObservable(this)
  }

  activate() {
    this.searchSubscription = this.textSearcher.debouncedValue$.subscribe()
  }

  setDefaultWorkspace() {
    if (
      this.canManageAll &&
      this.workspaceSelectorState.isDefaultToAllWorkspaces
    ) {
      return this.selectWorkspace(Workspace.none)
    }

    const memberWorkspace = this.getCurrentMember().workspace

    return this.selectWorkspace(
      this.workspaces.some((workspace) => workspace.id === memberWorkspace.id)
        ? memberWorkspace
        : this.workspaces[0],
    )
  }

  deactivate() {
    this.searchSubscription.unsubscribe()
  }

  @computed
  get searchText() {
    return this.textSearcher.value
  }

  @computed
  get selectedWorkspace() {
    return this.workspaceFilter().value
  }

  @computed
  get filteredWorkspaces() {
    const searchText = this.searchText.toLowerCase()

    if (searchText) {
      return this.workspaces.filter((ws) =>
        ws.name.toLocaleLowerCase().includes(searchText),
      )
    }

    return this.workspaces
  }

  @computed
  get canManageAll() {
    return this.getCurrentUserWorkspaceMemberships().some(({ member }) =>
      member.hasPermission('ADMIN_GLOBAL_USER_LIST'),
    )
  }

  @computed
  get workspaces() {
    return this.getCurrentUserWorkspaceMemberships()
      .filter(({ member }) => member.hasPermission('ADMIN_USER_LIST'))
      .map((member) => member.workspace)
  }

  @action.bound
  selectWorkspace(workspace: Workspace) {
    this.workspaceFilter().change(workspace)
    this.clearSelectedUsers()
    this.clearWorkspaceScopedFilters()
  }

  @action.bound
  setSearchText(searchText: string) {
    this.textSearcher.change(searchText)
  }

  @action.bound
  startEditing() {
    this.editing = true
  }

  @action.bound
  stopEditing() {
    this.editing = false
  }
}
